import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, KeyRound, Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const BrandIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5 4h14v4.2L8.7 20H5.2l9.1-10.4H5V4z" />
  </svg>
);

export default function SetupPasswordPage() {
  const navigate = useNavigate();
  const { user, setUserData } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);

  const confettiPieces = useMemo(
    () =>
      Array.from({ length: 28 }, (_, index) => ({
        id: index,
        left: `${(index * 13) % 100}%`,
        delay: `${(index % 8) * 0.08}s`,
        duration: `${2.6 + (index % 5) * 0.22}s`,
        rotate: `${(index % 2 === 0 ? 1 : -1) * (12 + index * 7)}deg`,
        color: ['#7c3aed', '#22c55e', '#0ea5e9', '#f59e0b'][index % 4],
      })),
    []
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => setShowConfetti(false), 3200);
    return () => window.clearTimeout(timeout);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('As senhas não conferem.');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API}/auth/setup-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Não foi possível criar sua senha.');
      }
      if (data.user) {
        setUserData(data.user);
      }
      toast.success('Senha criada com sucesso!');
      navigate('/app', { replace: true });
    } catch (error) {
      toast.error(error.message || 'Erro ao criar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#eef6ff,_#fafafa_55%,_#ffffff)] flex items-center justify-center px-4 py-10 overflow-hidden relative">
      {showConfetti ? (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          {confettiPieces.map((piece) => (
            <span
              key={piece.id}
              className="absolute top-[-10%] h-3 w-2 rounded-full opacity-90"
              style={{
                left: piece.left,
                backgroundColor: piece.color,
                transform: `rotate(${piece.rotate})`,
                animation: `zaptConfettiFall ${piece.duration} ease-out ${piece.delay} forwards`,
              }}
            />
          ))}
        </div>
      ) : null}

      <style>{`
        @keyframes zaptConfettiFall {
          0% { transform: translate3d(0, -20px, 0) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translate3d(0, 110vh, 0) rotate(540deg); opacity: 0; }
        }
      `}</style>

      <Card className="w-full max-w-md border-zinc-200 shadow-xl shadow-sky-100/60 relative">
        <CardHeader className="text-center">
          <div className="mb-5 flex items-center justify-center">
            <div className="inline-flex items-center gap-3 rounded-full border border-sky-100 bg-white px-4 py-2 shadow-sm shadow-sky-100/70">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 via-blue-500 to-violet-600 text-white">
                <BrandIcon className="h-5 w-5" />
              </div>
              <div className="text-left">
                <p className="text-lg font-semibold leading-none text-zinc-900">Zapt Resumo</p>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-700">
                  Primeiro acesso
                </p>
              </div>
            </div>
          </div>
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-100 via-violet-100 to-emerald-100">
            <KeyRound className="h-7 w-7 text-sky-600" />
          </div>
          <div className="mb-3 inline-flex mx-auto items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
            <Sparkles className="h-4 w-4" />
            Parabéns! Seja bem-vindo(a) ao Zapt Resumo
          </div>
          <CardTitle>Crie sua senha</CardTitle>
          <CardDescription>
            {user?.name ? `${user.name}, ` : ''}defina sua senha para acessar o Zapt Resumo nas próximas vezes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nova senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="password"
                  type="password"
                  className="pl-10"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Mínimo de 6 caracteres"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="confirmPassword"
                  type="password"
                  className="pl-10"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="Repita a senha"
                />
              </div>
            </div>
            <Button type="submit" className="w-full rounded-full" disabled={loading}>
              {loading ? 'Salvando...' : 'Entrar no Dashboard'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
